import { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  REQUIREMENT_DOC_SECTIONS,
  REQUIREMENT_DOC_SECTION_LABELS,
  type RequirementSection,
} from '@ispot/shared'

interface Props {
  value: RequirementSection
  onChange: (next: RequirementSection) => void
}

/**
 * Compact dropdown that swaps the document section an epic belongs to.
 * Renders inline next to the "Epic" kicker label. Closes on outside click,
 * Esc, or selection.
 */
export function EpicSectionSelector({ value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    function key(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', handle)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        className="inline-flex items-center gap-0.5 rounded px-1 py-[1px] text-[10.5px] hover:bg-warm-100"
        style={{
          color: 'var(--color-fg-muted)',
          background: open ? 'var(--color-warm-100)' : 'transparent',
          border: 0,
          fontWeight: 500,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Move to a different section"
      >
        <span>{REQUIREMENT_DOC_SECTION_LABELS[value]}</span>
        <ChevronDown size={9} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Document section"
          className="absolute z-50 left-0 top-full mt-1 rounded-md py-1"
          style={{
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 4px 12px rgba(29, 26, 21, 0.08)',
            minWidth: 180,
          }}
        >
          {REQUIREMENT_DOC_SECTIONS.map(s => {
            const active = s === value
            return (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={active}
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(s)
                  setOpen(false)
                }}
                className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-warm-100"
                style={{
                  color: active ? 'var(--color-fg)' : 'var(--color-fg-secondary)',
                  fontWeight: active ? 500 : 400,
                  background: active ? 'var(--color-warm-100)' : 'transparent',
                  border: 0,
                  cursor: 'pointer',
                }}
              >
                {REQUIREMENT_DOC_SECTION_LABELS[s]}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
